<template>
    <v-layout column>
        <v-toolbar color="primary" class="white--text">
            <v-toolbar-title>Scouting</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-icon large dark>bug_report</v-icon>
        </v-toolbar>
        <v-layout :row="$vuetify.breakpoint.mdAndUp" :column="$vuetify.breakpoint.smAndDown" wrap>
            <!-- Sync Indicators -->
            <v-flex>
                <v-layout row wrap align-center :justify-center="$vuetify.breakpoint.smAndDown" :justify-end="$vuetify.breakpoint.mdAndUp" fill-height>
                    <v-btn round dark :color="filterVisible ? 'error' : 'primary'" @click="toggleFilter">
                        {{ filterVisible ? 'Hide Filter' : 'Show Filter' }}
                    </v-btn>
                    <unsynced-status-indicator name="Unsynced" :unsyncedCount="unsyncedScoutEntriesCount"></unsynced-status-indicator>
                    <v-chip v-if="syncErrors.length > 0" color="error" text-color="white" disabled @click="showSyncErrors">
                        <v-avatar class="white font-weight-bold error--text">{{ syncErrors.length }}</v-avatar>
                        <span>Sync Errors</span>
                    </v-chip>
                    <v-btn icon v-if="loading">
                        <v-layout align-center justify-center>
                            <v-progress-circular indeterminate></v-progress-circular>
                        </v-layout>
                    </v-btn>
                    <v-btn icon color="error" @click="syncUnsyncedData()" v-else-if="unsyncedScoutEntriesCount > 0">
                        <v-icon>refresh</v-icon>
                    </v-btn>
                    <v-btn icon color="primary" v-else>
                        <v-icon>check</v-icon>
                    </v-btn>
                </v-layout>
            </v-flex>
         </v-layout>

        <div v-if="filterVisible">
            <!-- Plant, Division and Location Selects -->
            <ScoutingLocationSelection></ScoutingLocationSelection>

            <v-layout row wrap>
                <v-flex xs12>
                    <v-layout  justify-center="true" row wrap align-center> 
                        <v-flex xs12 sm2 px-1 >
                            <v-btn block color="primary" @click="clearFilter">Clear Filter</v-btn>
                        </v-flex>
                        <v-flex xs12 sm2 px-1 >
                            <v-btn block color="primary" :disabled="!selectedLocation" @click="addScout()">Add Scouting</v-btn>
                        </v-flex>
                    </v-layout>
                </v-flex>
            </v-layout>
        </div>

        <!-- Scout Entries Table -->
        <v-layout row wrap>
            <v-flex xs12>
                <div :style="gridSectionStyle">
                    <ScoutingManagementGrid :tableLoading="tableLoading" @editScout="editScout"></ScoutingManagementGrid>
                </div>
            </v-flex>
        </v-layout> 
        
        <ScoutingAddEditDialog v-if="showAddEditScoutDialog" :initialData="editedScouting" @cancelAddEditScout="hideAddEditScoutDialog"></ScoutingAddEditDialog>

        <ScoutingSyncErrorDialog v-if="showSyncErrorDialog" :syncErrors="syncErrors" @close="hideSyncErrors">
        </ScoutingSyncErrorDialog>

    </v-layout>
</template>

<script>
import ScoutingManagementGrid from "@/components/scout/ScoutingManagementGrid.vue";
import ScoutingLocationSelection from "@/components/scout/ScoutingLocationSelection.vue";
import ScoutingAddEditDialog from "@/components/scout/ScoutingAddEditDialog.vue";
import UnsyncedStatusIndicator from '@/components/generic/UnsyncedStatusIndicator.vue';
import ScoutingSyncErrorDialog from '@/components/scout/ScoutingSyncErrorDialog.vue';

import UtilService from "@/services/utilService.js";
import moment from "moment";

export default {
    data() {
        return {
            debug: false,

            editedScouting: null,

            tableLoading: false,
            loading: false,

            showAddEditScoutDialog: false,
            showSyncErrorDialog: false,
            filterVisible: true,
        }
    },
    props: [
        
    ],
    components: {
        UnsyncedStatusIndicator,
        ScoutingManagementGrid,
        ScoutingLocationSelection,
        ScoutingAddEditDialog,
        ScoutingSyncErrorDialog
    },
    watch: {
        async selectedPlant(newVal, oldVal) {
            if (this.debug) console.log("selectedPlant changed from", oldVal);
            if (this.debug) console.log("selectedPlant changed to", newVal);

            if (newVal != oldVal && newVal) {
                // reload the data - we need to make sure that the correct data is being made available for offline storage
                this.tableLoading = true;

                // first attempt to sync any currently unsynced data
                await this.$store.dispatch("ScoutStore/syncUnsyncedData", {plantIDs: newVal});

                // reload both the scouting data, and the pest configuration data (wait until the previous call is complete before trying these)
                await Promise.all([     
                    this.$store.dispatch("ScoutStore/loadScoutings", {plantIDs: newVal}),               
                    this.$store.dispatch("ScoutStore/loadPestConfigurations", {plantIDs: newVal}),
                    this.$store.dispatch("TrapInspectionStore/loadTraps", newVal),
                    this.$store.dispatch("TrapInspectionStore/loadTrapInspectionsForPlant", { plantID: newVal }),
                    this.$store.dispatch("ScoutStore/loadScoutingCropStages")
                ]);

                this.tableLoading = false;
            }
        }
    },
    computed: {
        selectedPlant: {
            get() {
                return this.$store.state.PestAndDiseaseStore.selectedPlant;
            },
            set(value) {
                this.$store.commit("PestAndDiseaseStore/setSelectedPlant", value);
            }
        },
        selectedProductCategory: {
            get() {
                return this.$store.state.PestAndDiseaseStore.selectedProductCategory;
            },
            set(value) {
                this.$store.commit("PestAndDiseaseStore/setSelectedProductCategory", value);
            }
        },
        selectedDivision: {
            get() {
                return this.$store.state.PestAndDiseaseStore.selectedDivision;
            },
            set(value) {
                this.$store.commit("PestAndDiseaseStore/setSelectedDivision", value);
            }
        },
        selectedLocation: {
            get() {
                return this.$store.state.PestAndDiseaseStore.selectedLocation;
            },
            set(value) {
                this.$store.commit("PestAndDiseaseStore/setSelectedLocation", value);
            }
        },
        unsyncedScoutEntriesCount() {
            return this.$store.getters["ScoutStore/getTotalUnsyncedScoutings"];
        },
        syncErrors(){
            return this.$store.getters["ScoutStore/getSyncErrors"];
        },
        gridSectionStyle() {
            return {
                height: this.filterVisible ? '70vh' : '81vh', // optimised on 1440p display
                overflowY: 'auto'
            };
        },  
    },
    methods: {
        async syncUnsyncedData() {
            this.loading = true;

            try {
                await this.$store.dispatch("ScoutStore/syncUnsyncedData",  { plantIDs: [this.selectedPlant]});
            } catch(err){
                this.showSyncError();                
            }

            this.loading = false;
        },
        showSyncError(){
            alert("Unable to sync.");
        },

        addScout() {
            let userID = this.$store.getters["UserStore/getPersonnelID"];

            this.editedScouting = {
                ScoutingID: null,
                Date: UtilService.formatDateYMD(moment()), // default to today's date
                LocationID: this.selectedLocation,
                DivisionID: this.selectedDivision,
                PlantID: this.selectedPlant,
                ScoutingSpotChecks: [],
                FruitOnPlants: false,
                Closed: false,
                Notes: null,
                SpotChecks: 5, // Default Number
                CreatedBy: userID
            };

            // setting this to true causes the child component to render, creating it, and hence it will take a copy of this object for editing purposes
            this.showAddEditScoutDialog = true;
        },
        editScout(scouting) {
            this.editedScouting = scouting;

            // setting this to true causes the child component to render, creating it, and hence it will take a copy of this object for editing purposes
            this.showAddEditScoutDialog = true;
        },
        hideAddEditScoutDialog() {
            this.editedScouting = null;
            this.showAddEditScoutDialog = false;
        },

        clearFilter() {
            this.selectedPlant = null;
            this.selectedDivision = null;
            this.selectedLocation = null;
            this.selectedProductCategory = null;
            this.filter = {};

            this.$store.commit("ScoutStore/updateScoutings", []);
        },
        showSyncErrors(){
            this.showSyncErrorDialog = true;
        },
        hideSyncErrors(){
            this.showSyncErrorDialog = false;
        },
        toggleFilter(){
            this.filterVisible = !this.filterVisible;
        }
    },
    async created() {
        
    }
}
</script>

<style>

</style>
